import {createContext, useCallback, useContext, useState} from 'react'
import {useBoolean} from 'usehooks-ts'

import {noop} from '@sdox/utils'

import type {Editor} from '@tiptap/react'
import type {PropsWithChildren} from 'react'
import type {EditorFeature} from './types'

export enum ControlsPosition {
  FIXED = 'fixed',
  FLOATING = 'floating',
}

export interface EditorContextState {
  editor: Editor
  features: EditorFeature[]
  maxCharCount?: number
  controlsPosition: ControlsPosition
  showDebugger: boolean
  showLinkDialog: boolean
  showFootnoteDialog: boolean
  toggleDebugger: () => void
  toggleLinkDialog: () => void
  toggleFootnoteDialog: () => void
  showBubbleMenu: () => boolean
  showFixedMenu: () => boolean
  toggleControlsPosition: () => void
  hasFeature: (feature: EditorFeature) => boolean
}

interface UseContextValuesParams {
  features: EditorContextState['features']
  maxCharCount?: number
}

interface EditorContextProviderProps extends PropsWithChildren {
  value: EditorContextState
}

const EditorContext = createContext<EditorContextState>({
  editor: null!,
  features: [],
  controlsPosition: ControlsPosition.FIXED,
  showDebugger: false,
  showLinkDialog: false,
  showFootnoteDialog: false,
  toggleDebugger: noop,
  toggleLinkDialog: noop,
  toggleFootnoteDialog: noop,
  showBubbleMenu: () => false,
  showFixedMenu: () => true,
  toggleControlsPosition: noop,
  hasFeature: () => false,
})

export const useEditorContextValues = ({features, maxCharCount}: UseContextValuesParams) => {
  const [controlsPosition, setControlsPosition] = useState<ControlsPosition>(ControlsPosition.FIXED)
  const {value: showDebugger, toggle: toggleDebugger} = useBoolean(false)
  const {value: showLinkDialog, toggle: toggleLinkDialog} = useBoolean(false)
  const {value: showFootnoteDialog, toggle: toggleFootnoteDialog} = useBoolean(false)

  const toggleControlsPosition = () => {
    if (controlsPosition === ControlsPosition.FIXED) {
      setControlsPosition(ControlsPosition.FLOATING)
    } else {
      setControlsPosition(ControlsPosition.FIXED)
    }
  }

  const hasFeature = useCallback((feature: EditorFeature): boolean => {
    if (features.length === 0) return true
    return features.includes(feature)
  }, [features])

  const context: Omit<EditorContextState, 'editor'> = {
    features,
    maxCharCount,
    controlsPosition,
    showDebugger,
    showLinkDialog,
    showFootnoteDialog,
    showBubbleMenu: () => controlsPosition === ControlsPosition.FLOATING,
    showFixedMenu: () => controlsPosition === ControlsPosition.FIXED,
    toggleDebugger,
    toggleLinkDialog,
    toggleFootnoteDialog,
    toggleControlsPosition,
    hasFeature,
  }

  return context
}

export const EditorContextProvider = ({children, value}: EditorContextProviderProps) => {
  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  )
}

export const useTipTapEditorContext = () => {
  const ctx = useContext(EditorContext)
  if (!ctx) {
    throw new Error('useEditorContext must be used inside EditorContext')
  }
  return ctx
}
